import type { EngineSnapshot } from './engine';

export class WorkoutSpeech {
  private synth: SpeechSynthesis | null;
  private muted = false;
  private rate = 1;
  private voiceURI = '';
  private voices: SpeechSynthesisVoice[] = [];
  private lastText = '';
  private onVoices: (voices: SpeechSynthesisVoice[]) => void;

  constructor(onVoices: (voices: SpeechSynthesisVoice[]) => void = () => undefined) {
    this.synth = typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null;
    this.onVoices = onVoices;
    this.refreshVoices = this.refreshVoices.bind(this);
    this.synth?.addEventListener('voiceschanged', this.refreshVoices);
    this.refreshVoices();
  }

  get available(): boolean { return Boolean(this.synth && typeof SpeechSynthesisUtterance !== 'undefined'); }
  get lastCue(): string { return this.lastText; }

  configure(muted: boolean, voiceURI: string, rate: number): void {
    this.muted = muted;
    this.voiceURI = voiceURI;
    this.rate = rate;
  }

  refreshVoices(): void {
    this.voices = this.synth?.getVoices() ?? [];
    if (!this.voiceURI) this.voiceURI = this.preferredVoice()?.voiceURI ?? '';
    this.onVoices(this.voices);
  }

  preferredVoice(): SpeechSynthesisVoice | undefined {
    const english = this.voices.filter((v) => /^en[-_]/i.test(v.lang));
    return english.find((v) => v.localService && /samantha|ava|serena|daniel|karen/i.test(v.name))
      ?? english.find((v) => v.localService && /apple/i.test(v.name))
      ?? english.find((v) => v.localService)
      ?? english[0];
  }

  speak(text: string, interrupt = false): void {
    this.lastText = text;
    if (!this.available || this.muted || !text) return;
    if (interrupt) this.synth?.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = this.rate;
    utterance.volume = 1;
    utterance.voice = this.voices.find((v) => v.voiceURI === this.voiceURI) ?? this.preferredVoice() ?? null;
    this.synth?.speak(utterance);
  }

  replay(): void { if (this.lastText) this.speak(this.lastText, true); }
  cancel(): void { this.synth?.cancel(); }

  cueForStep(snapshot: EngineSnapshot): string {
    const { step } = snapshot;
    if (step.kind === 'welcome') return 'Get ready. Clear some space. Stop for sharp pain or dizziness.';
    if (step.kind === 'prepare') return `${step.title}. ${step.detail}`;
    if (step.kind === 'rest') return `Rest. Round ${step.round} complete. ${Math.ceil(step.durationMs / 1000)} seconds.`;
    if (step.kind === 'complete') return 'Workout complete. Nice work.';
    if (step.kind === 'reps') return `${step.title}. ${step.reps}${step.eachSide ? ` total, alternating for ${Math.floor((step.reps ?? 0) / 2)} each leg` : ' reps'}. Follow the steady cadence.`;
    return `${step.title}. ${Math.ceil(step.durationMs / 1000)} seconds. Start.`;
  }
}
